// Strip + revealed panel layout (post-lead-capture).
//
// The TOP STRIP shows only Location (zip/city search or selected pill).
// Optional strip fields live in FittingTypeField.tsx and DateFieldStrip.tsx.
// Selecting a location reveals the full panel below — fitting type, date,
// fitter, times, and Book Fitting — with progressive enablement:
//   location → fitting type → date + fitter → times → book CTA.
//
// Design comp reference: screenshots 2026-05-25 6:18:01 through 6:18:42.

import { useEffect, useRef, useState } from 'react';
import styles from '../styles/widget.module.css';
import { listLocations } from '../api/client';
import { geocodeZip } from '../utils/geocode';
import { haversineMiles } from '../utils/distance';
import { FITTING_TYPES } from '../data/fittingTypes';
import { getBookingPanelEnablement } from '../utils/bookingFlow';
import { MobileSelectionSummary } from '../components/MobileSelectionSummary';
import { FitterSelect } from './FitterSelect';
import { FitterField } from '../components/FitterField';
import { TimeSlots } from './TimeSlots';
import { PromoConfirm } from './PromoConfirm';
import { Calendar } from './Calendar';
import type { Location } from '../types';
import { useClickOutside } from '../hooks/useClickOutside';
import type { BookingAction, BookingState } from '../state/machine';
import { useOpenClose } from '../hooks/useOpenClose';
import InfoIcon from '../assets/icons/Info'
import SimplePopup from '../components/SimplePopup';
import { FittingTypeField } from '../components/FittingTypeField';
import { DateFieldStrip } from '../components/DateFieldStrip';
import { useAvailableDates } from '../hooks/useAvailableDates';
import { AnchoredPortal } from '../components/AnchoredPortal';

interface BookingStripPanelProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onBook: () => void;
  /** Called from TimeSlots' no-availability empty state to POST /waitlist. */
  onJoinWaitlist: () => void;
  layoutType?: 'desktop' | 'mobile'
}

const MAX_RADIUS_MILES = 100;

export function BookingStripPanel({ state, dispatch, onBook, onJoinWaitlist, layoutType }: BookingStripPanelProps) {
  const { isOpen, toggle, close } = useOpenClose()
  const enablement = getBookingPanelEnablement(state);
  const isMobile = layoutType === 'mobile';
  const hideSelectedPill = isMobile;

  return (
    <>
      {/* Top strip — always visible */}
      <div className={styles.stripWrapper}>
        <div className={styles.strip}>
          <div className={styles.stepHeader}>
            <div className={styles.stripTitle}>
              <h2 className={styles.heading}>Book Your Custom Fitting</h2>
              <p className={styles.subtitle} style={{ margin: 0 }}>
                World&apos;s #1 Custom Club Fitter <button className={styles.infoPopupTrigger} onClick={toggle}><InfoIcon /></button>
              </p>
              <SimplePopup
                children={
                  <div>
                    <h3>94% SATISFACTION RATE</h3>
                    <p>Club Champion has an industry leading 94% satisfaction rate across all handicap levels. Golf Digest has named us America’s premier fitter every year over the past decade. Come experience a tour-level, brand agnostic fitting with over 65,000 possible club head and shaft combinations from all the leading manufacturers to see what we can do to improve your game.</p>
                  </div>
                }
                isOpen={isOpen}
                onClosePopup={close}
              />
            </div>
            {isMobile && <MobileSelectionSummary state={state} dispatch={dispatch} />}
          </div>
          {!isMobile && <LocationField state={state} dispatch={dispatch} />}
          {isMobile &&
            <>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="location">
                <LocationField
                  state={state}
                  dispatch={dispatch}
                  hideSelectedPill={hideSelectedPill}
                />
              </div>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="fittingType">
                <FittingTypeField
                  state={state}
                  dispatch={dispatch}
                  disabled={!enablement.fittingTypeEnabled}
                  hideSelectedPill={hideSelectedPill}
                />
              </div>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="date">
                <DateFieldStrip
                  state={state}
                  dispatch={dispatch}
                  disabled={!enablement.dateAndFitterEnabled}
                  hideSelectedPill={hideSelectedPill}
                />
              </div>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="fitter">
                <FitterField
                  state={state}
                  dispatch={dispatch}
                  disabled={!enablement.dateAndFitterEnabled}
                  hideSelectedPill={hideSelectedPill}
                />
              </div>
              <div className={styles.mobileFieldWrapper} data-booking-mobile-field="time">
                <TimeSlotsPanelWrapper
                  state={state}
                  dispatch={dispatch}
                  onJoinWaitlist={onJoinWaitlist}
                  disabled={!enablement.timeEnabled}
                  hideSelectedPill={state?.selectedTime ? true : false}
                />
              </div>
              <PromoConfirm state={state} dispatch={dispatch} onConfirmFlow={onBook} />
            </>
          }
        </div>
      </div>

      {/* Lower panel — revealed as soon as a location is selected */}
      {enablement.showPanel && layoutType === 'desktop' && (
        <div className={styles.panel}>
          <div className={styles.panelGrid}>
            <FittingTypePanel state={state} dispatch={dispatch} />
            <CalendarFitterPanel
              state={state}
              dispatch={dispatch}
              disabled={!enablement.dateAndFitterEnabled}
            />
            <TimeSlotsPanelWrapper
              state={state}
              dispatch={dispatch}
              onJoinWaitlist={onJoinWaitlist}
              disabled={!enablement.timeEnabled}
            />
            <div className={styles.panelFooter}>
              <PromoConfirm state={state} dispatch={dispatch} onConfirmFlow={onBook} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ──────────────────────────────────────────────────────────────
   STRIP FIELDS
   ────────────────────────────────────────────────────────────── */

function LocationField({
  state,
  dispatch,
  hideSelectedPill = false,
}: {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  hideSelectedPill?: boolean;
}) {
  const [allLocations, setAllLocations] = useState<Location[] | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(state.locationQuery);
  const [ranked, setRanked] = useState<RankedLocation[]>([]);
  // AnchoredPortal positions the dropdown using this ref's bounding rect.
  const inputRef = useRef<HTMLInputElement>(null);

  const wrapperRef = useClickOutside<HTMLDivElement>(() => {
    setOpen(false);
  }, {
    enabled: open,
    // The dropdown is portaled to <body>, so its rows are NOT inside
    // wrapperRef. Without this exemption a mousedown on a dropdown row
    // fires useClickOutside before the row's onClick gets a chance to
    // dispatch the selection.
    ignoreSelector: '[data-cc-dropdown-portal]',
  });

  useEffect(() => {
    if (!allLocations) listLocations().then(setAllLocations).catch(() => setAllLocations([]));
  }, [allLocations]);


  useEffect(() => {
    if (!allLocations) return;
    rankLocations(query, allLocations).then(setRanked);
  }, [query, allLocations]);

  // PILL — when a location is selected
  if (state.selectedLocation) {
    if (hideSelectedPill) return null;
    return (
      <div className={styles.stripPill}>
        <span className={styles.label}>Nearest Studio</span>
        <div className={styles.stripPillValue}>
          <span>{state.selectedLocation.name}</span>
          <button
            className={styles.stripPillChange}
            onClick={() => {
              setQuery('')
              dispatch({ type: 'SELECT_LOCATION', payload: null })
            }}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  // DROPDOWN — when no location is selected. Dropdown options render via
  // AnchoredPortal so they aren't clipped by the modal's overflow:auto
  // wrapper. Position tracks the input's bounding rect on scroll/resize.
  return (
    <div className={styles.field} ref={wrapperRef}>
      <label className={styles.label}>Nearest Studio</label>
      <input
        ref={inputRef}
        className={styles.input}
        type="text"
        placeholder="Zip Code or City Name"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          dispatch({ type: 'SET_LOCATION_QUERY', payload: e.target.value });
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      <AnchoredPortal anchorRef={inputRef} isOpen={open && ranked.length > 0}>
        <div className={styles.locationDropdown} data-cc-dropdown-portal="location">
          <div className={styles.locationDropdownHeader}>
            {ranked.length} Locations within {MAX_RADIUS_MILES} miles
          </div>
          {ranked.slice(0, 6).map((loc) => (
            <div
              key={loc.id}
              className={styles.locationRow}
              onClick={() => {
                dispatch({ type: 'SELECT_LOCATION', payload: loc });
                dispatch({ type: 'SET_NEARBY_LOCATIONS', payload: ranked });
                setOpen(false);
              }}
            >
              <div className={styles.locationName}>
                {loc.name.toUpperCase()}
              </div>
              {loc.address && <div className={styles.locationAddress}>{loc.address}</div>}
              {loc.distanceMiles !== undefined && (
                <div className={styles.locationDistance}>
                  {Math.round(loc.distanceMiles)} miles away
                </div>
              )}
            </div>
          ))}
        </div>
      </AnchoredPortal>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
   PANEL COLUMNS (revealed after location selection)
   ────────────────────────────────────────────────────────────── */

function FittingTypePanel({ state, dispatch }: { state: BookingState; dispatch: React.Dispatch<BookingAction> }) {
  return (
    <div>
      <h3 className={styles.label}>Fitting Type</h3>
      <div className={styles.fittingTypeList}>
        {FITTING_TYPES.map((ft) => {
          const selected = state.selectedFittingType?.id === ft.id;
          return (
            <div
              key={ft.id}
              className={`${styles.fittingTypeCard} ${selected ? styles.selected : ''}`}
              onClick={() => dispatch({ type: 'SELECT_FITTING_TYPE', payload: ft })}
            >
              <div className={styles.fittingTypeHeader}>
                <span className={styles.fittingTypeName}>{(ft.displayName ?? ft.name).toUpperCase()}</span>
                {ft.badge && <span className={styles.fittingTypeBadge}>{ft.badge}</span>}
              </div>
              <div className={styles.fittingTypeDescription}>{ft.description}</div>
              <div className={styles.fittingTypeMeta}>
                <span>{ft.durationLabel}</span>
                <span>{ft.pricingLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarFitterPanel({
  state,
  dispatch,
  disabled,
}: {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  disabled: boolean;
}) {
  const today = new Date();
  const min = today.toISOString().split('T')[0];
  const max = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const { availableDates, loading } = useAvailableDates(
    state.selectedLocation,
    state.selectedFittingType,
    state.selectedFitterId,
  );

  return (
    <div >
      {/* Desktop: visible calendar grid (always exposed). Mobile: native date input.
          Visibility is toggled via CSS — see widget.module.css [data-cc-layout]
          rules near .calendar / .calendarMobileInput. */}
      <div className={`${styles.calendarDesktop} ${disabled ? styles.panelSectionDisabled : ''}`}>
        <h3 className={styles.label}>Fitting Date</h3>
        {disabled && <span className={styles.disabledFieldMessage}>Select a fitting type to enable this field</span>}
        <Calendar
          selectedDate={state.selectedDate}
          onChange={(d) => dispatch({ type: 'SELECT_DATE', payload: d })}
          locationName={state.selectedLocation?.name}
          availableDates={availableDates}
          loading={loading}
        />
      </div>
      <div className={styles.calendarMobileInput}>
        <input
          className={styles.input}
          type="date"
          min={min}
          max={max}
          value={state.selectedDate ?? ''}
          disabled={disabled}
          onChange={(e) => dispatch({ type: 'SELECT_DATE', payload: e.target.value || null })}
        />
      </div>
      <FitterSelect state={state} dispatch={dispatch} disabled={disabled} />
    </div>
  );
}

function TimeSlotsPanelWrapper({
  state,
  dispatch,
  onJoinWaitlist,
  disabled,
  hideSelectedPill = false
}: {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onJoinWaitlist: () => void;
  hideSelectedPill?: boolean;
  disabled: boolean;
}) {

  if (hideSelectedPill) return null

  if (disabled) {
    return (
      <div className={styles.panelSectionDisabled}>
        <h3 className={styles.label}>Fitting Time</h3>
        <span className={styles.disabledFieldMessage}>Select a fitting type and date to see available times</span>
      </div>
    );
  }



  return (
    <div>
      <h3 className={styles.label}>Fitting Time</h3>
      <TimeSlots
        state={state}
        dispatch={dispatch}
        onJoinWaitlist={onJoinWaitlist}
        disabled={disabled}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   HELPERS
   ────────────────────────────────────────────────────────────── */

interface RankedLocation extends Location {
  distanceMiles?: number;
}

async function rankLocations(query: string, all: Location[]): Promise<RankedLocation[]> {
  const trimmed = query.trim();
  if (trimmed.length < 3) return [];

  if (/^\d{5}$/.test(trimmed)) {
    const geo = await geocodeZip(trimmed);
    if (geo) {
      return all
        .map((loc) => {
          if (loc.lat === undefined || loc.lng === undefined) return null;
          const distanceMiles = haversineMiles(geo, { lat: loc.lat, lng: loc.lng });
          if (distanceMiles > MAX_RADIUS_MILES) return null;
          return { ...loc, distanceMiles };
        })
        .filter((l): l is RankedLocation & { distanceMiles: number } => l !== null)
        .sort((a, b) => a.distanceMiles - b.distanceMiles);
    }
  }

  const q = trimmed.toLowerCase();
  return all
    .filter((loc) => `${loc.name} ${loc.address ?? ''}`.toLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name));
}
